import { Dictionary, isEqual } from 'lodash';
import { useMemo, useRef, useState } from 'react';
import { get } from 'react-hook-form';
import { IEvaluationVariables } from '../../evaluate/variables/IEvaluationVariables';
import { IAssignableExpression } from '../assignable/IAssignableExpression';
import { buildAsyncServiceParams } from './buildAsyncServiceParams';
import { AsyncServiceMethod, IAsyncService } from './context/IAsyncService';
import { parseAsyncServiceUrl } from './url/parseAsyncServiceUrl';

export type IAsyncServiceInputConfig = Dictionary<IAssignableExpression>;

export enum AsyncServiceFetchResultStatus {
  SUCCESS = 'success',
  SKIPPED = 'skipped',
  ERROR = 'error',
}

function isNested(output: string) {
  return output.includes('.');
}

/**
 * Estrae il valore dei campi annidati nell'oggetto ritornato dai servizi
 * e lo rimappa in root, utilizzando la chiave ricostruita.
 */
function mapResult(data: any, service: IAsyncService) {
  if (!service) return data;
  if (!data) return data;

  service.outputs.forEach(output => {
    if (isNested(output.sourcePath ?? output.name)) {
      // Prendiamo il valore originale, definito in sourceField se presente, altrimenti già nel name
      const value = get(data, output.sourcePath ?? output.name);
      // Rimappiamo il valore in root (name = <padre>@@DOT@@<figlio>))
      data[output.name ?? output.sourcePath] = value;
    }
  });

  return data;
}

/**
 * Ritorna la funzione che permette di chiamare il servizio asincrono
 * configurato, gestendone il risultato e lo stato di caricamento e errore.
 */
export function useAsyncServiceFetch(props: {
  service: IAsyncService | null | undefined;
  axiosClient: ((config: any) => Promise<any>) | undefined | null;
  /** Mappa tra i parametri di input della chiamata al servizio e i widget del form */
  inputs: IAsyncServiceInputConfig | null | undefined;
  disabled?: boolean;
}) {
  const { service, axiosClient, inputs, disabled } = props;

  const [data, setData] = useState<any[] | null>(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleFunction = useAsyncServiceFunction(service, axiosClient);

  const lastBody = useRef<Record<string, any> | null>(null);

  const fetchCount = useRef(0);

  const handleFetch = useMemo(() => {
    if (disabled) return null;
    // Costruisco la funzione solo se tutti i componenti sono presenti
    if (!service || !inputs || !axiosClient) return null;

    if (!handleFunction) return null;

    return async (searchParam: any, variables: IEvaluationVariables) => {
      setLoading(true);
      try {
        const params = buildAsyncServiceParams(service, inputs, variables);

        const body = { ...searchParam, ...params };

        // Se il body della chiamata è lo stesso della precedente, non faccio nulla
        if (isEqual(body, lastBody.current)) {
          return;
        }

        lastBody.current = body;

        const resultData = await handleFunction(body);
        setData(resultData ?? []);
        setError(null);

        // NB: è importante che sia chiamato per ultimo per evitare errori
        // di sincronizzazione (es che il fetch venga aumentato prima di aver effettivamente
        // completato la chiamata)
        fetchCount.current += 1;
      } catch (error: any) {
        console.warn(`Errore durante la chiamata al servizio ${service.name} (ID#${service.id})`, error); // prettier-ignore
        setError(error);
        fetchCount.current += 1;
      } finally {
        setLoading(false);
      }
    };
  }, [axiosClient, disabled, handleFunction, inputs, service]);

  return { data, loading, error, handleFetch, fetchCount };
}

/**
 * Ritorna la funzione da chiamare per eseguire il servizio asincrono
 * configurato.
 */
export function useAsyncServiceFunction(
  service: IAsyncService | null | undefined,
  axiosClient: ((config: any) => Promise<any>) | undefined | null,
) {
  const handleFunction = useMemo(() => {
    if (!service || !axiosClient) return null;

    return async (params: any) => {
      const { url, originalParams } = parseAsyncServiceUrl(service);

      const response = await axiosClient({
        url,
        method: service.method,
        params:
          // GET
          service.method === AsyncServiceMethod.GET
            ? { ...originalParams, ...params }
            : // Gli originalParams vengono direttamente dall'URL, quindi vanno sempre qui
              originalParams,
        data:
          // POST-PUT
          service.method !== AsyncServiceMethod.GET ? params : undefined,
      });

      // `apiClient` di `@framework/api-client` restituisce la AxiosResponse,
      // che contiene i dati nel campo `data`
      const data = response?.data;

      const resultData = service.outputRoot
        ? get(data, service.outputRoot)
        : data;

      if (Array.isArray(resultData)) {
        return resultData.map(item => mapResult(item, service));
      } else {
        return mapResult(resultData, service);
      }
    };
  }, [axiosClient, service]);

  return handleFunction;
}
